import { formatDateTime, formatDayMonth, type Note, noteInputSchema, routes } from '@pmai/contracts';
import {
  Button,
  CheckboxField,
  ConfirmDialog,
  EmptyState,
  FormDialog,
  IconButton,
  Spinner,
  StatusBadge,
  TextAreaField,
  TextField,
  cn,
} from '@pmai/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { Can, usePermissions } from '../../shared/auth/session';
import { fromDateTimeLocal, toDateTimeLocal } from '../../shared/dates';
import { bind } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';
import { useConfirm } from '../../shared/use-confirm';

/**
 * The one notes component (ADR 0010): project card, dashboard notes dialog (FR-PRJ-026…032,
 * FR-DASH-011) and customer card (FR-CUST-017…019). The variants differ only where the
 * requirements do: date required and archiving for projects, full date for customers.
 */
export type NotesOwner = { kind: 'project'; id: string } | { kind: 'customer'; id: string };

const listRoute = (owner: NotesOwner) =>
  owner.kind === 'project' ? routes.notes.listForProject : routes.notes.listForCustomer;
const createRoute = (owner: NotesOwner) =>
  owner.kind === 'project' ? routes.notes.createForProject : routes.notes.createForCustomer;

export function NotesPanel({ owner }: { owner: NotesOwner }) {
  const { t } = useTranslation();
  const isProject = owner.kind === 'project';
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Note | 'new' | null>(null);
  const list = listRoute(owner);
  const notes = useApiQuery(list, {
    params: { id: owner.id },
    query: { includeArchived: showArchived ? 'true' : 'false' },
  });
  const canEdit = usePermissions().includes('notes.edit');
  const remove = useConfirm(routes.notes.remove, {
    invalidates: [list],
    success: 'notes.deleted',
    params: (n: Note) => ({ id: n.id }),
  });
  const archive = useApiMutation(routes.notes.setArchived, { invalidates: [list] });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isProject ? (
          <CheckboxField
            label={t('notes.showArchived')}
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
        ) : (
          <span />
        )}
        <Can perm="notes.edit">
          <Button size="sm" icon="plus" onClick={() => setEditing('new')}>
            {t('notes.add')}
          </Button>
        </Can>
      </div>
      {notes.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : !notes.data?.length ? (
        <EmptyState icon="notebook-pen" title={t(isProject ? 'notes.emptyProject' : 'notes.emptyCustomer')} />
      ) : (
        <ul aria-label={t('notes.content')} className="flex flex-col gap-3">
          {notes.data.map((n) => (
            <li
              key={n.id}
              className={cn(
                'rounded-lg border p-3',
                n.important ? 'border-amber-300 bg-amber-50/60' : 'border-border',
                n.archivedAt && 'opacity-60',
              )}
            >
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
                <span className="flex items-center gap-2">
                  <time dateTime={n.noteAt} title={formatDateTime(n.noteAt)}>
                    {isProject ? formatDayMonth(n.noteAt) : formatDateTime(n.noteAt)}
                  </time>
                  {n.important && <StatusBadge tone="warning" label={t('notes.importantBadge')} />}
                  {n.archivedAt && <StatusBadge tone="neutral" label={t('notes.archived')} />}
                </span>
                {canEdit && (
                  <span className="flex gap-1">
                    <IconButton icon="pencil" label={t('common.edit')} onClick={() => setEditing(n)} />
                    {isProject && (
                      <IconButton
                        icon={n.archivedAt ? 'archive-restore' : 'archive'}
                        label={t(n.archivedAt ? 'notes.unarchive' : 'notes.archive')}
                        onClick={() =>
                          archive.mutate({ params: { id: n.id }, body: { archived: !n.archivedAt } })
                        }
                      />
                    )}
                    <IconButton icon="trash-2" label={t('common.delete')} onClick={() => remove.ask(n)} />
                  </span>
                )}
              </div>
              <p className="whitespace-pre-line text-sm">{n.content}</p>
            </li>
          ))}
        </ul>
      )}
      <NoteDialog owner={owner} note={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...remove.dialog}
        title={t('notes.deleteTitle')}
        message={t('notes.deleteText')}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}

function NoteDialog({
  owner,
  note,
  onClose,
}: {
  owner: NotesOwner;
  note: Note | 'new' | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const requireDate = owner.kind === 'project'; // FR-PRJ-027; customers: empty = now (FR-CUST-017)
  const schema = useMemo(
    () =>
      z.object({
        content: noteInputSchema.shape.content,
        noteAt: requireDate ? z.string().min(1, { message: t('notes.dateRequired') }) : z.string(),
        important: z.boolean(),
      }),
    [requireDate, t],
  );
  const existing = note !== 'new' ? note : null;
  const { form, create, update, dialog } = useEntityForm({
    schema,
    open: note !== null,
    record: existing,
    values: existing
      ? { content: existing.content, noteAt: toDateTimeLocal(existing.noteAt), important: existing.important }
      : { content: '', noteAt: toDateTimeLocal(), important: false },
    create: createRoute(owner),
    update: routes.notes.update,
    invalidates: [listRoute(owner)],
    onClose,
    success: { create: 'notes.added', update: 'notes.updated' },
  });

  return (
    <FormDialog
      {...dialog}
      title={existing ? t('notes.editTitle') : t('notes.newTitle')}
      submitLabel={existing ? t('common.update') : t('notes.add')}
      onSubmit={form.handleSubmit((v) => {
        const body = { content: v.content, important: v.important, noteAt: fromDateTimeLocal(v.noteAt) };
        if (existing)
          update.mutate({ params: { id: existing.id }, body: { ...body, version: existing.version } });
        else create.mutate({ params: { id: owner.id }, body });
      })}
    >
      <TextField
        label={t('notes.noteAt')}
        type="datetime-local"
        required={requireDate}
        {...bind(form, 'noteAt')}
      />
      <CheckboxField label={t('notes.important')} {...form.register('important')} />
      <TextAreaField label={t('notes.content')} required rows={5} {...bind(form, 'content')} />
    </FormDialog>
  );
}
